import axios from "axios";
import {
  collectInvoicePayments,
  enrichLineItemsFromInvoiceTotals,
  getInvoicePaymentTotal,
} from "./appointmentInvoiceHelpers";

function countResolvedServiceNames(rows) {
  if (!Array.isArray(rows)) return -1;
  let c = 0;
  for (const row of rows) {
    const raw =
      row?.item_name ??
      row?.itemName ??
      row?.service_name ??
      row?.name ??
      "";
    const t = String(raw).trim();
    if (t && t !== "--" && t !== "-") c += 1;
  }
  return c;
}

export function pickInvoiceLineItems(data) {
  if (!data) return [];
  const candidates = [
    data.itemsList,
    data.invoice_items,
    data.invoiceItems,
    data.items,
  ].filter((a) => Array.isArray(a) && a.length > 0);
  if (candidates.length === 0) return [];
  let best = candidates[0];
  let bestScore = countResolvedServiceNames(best);
  for (let i = 1; i < candidates.length; i += 1) {
    const arr = candidates[i];
    const sc = countResolvedServiceNames(arr);
    if (sc > bestScore) {
      best = arr;
      bestScore = sc;
    }
  }
  return best;
}

export function buildServiceNameByIdMap(serviceMaster = []) {
  const map = new Map();
  if (!Array.isArray(serviceMaster)) return map;
  serviceMaster.forEach((s) => {
    const key = s?.medical_service_id ?? s?.id;
    const value = s?.name ?? s?.item_name ?? "";
    if (key != null && String(value).trim()) {
      map.set(String(key), String(value).trim());
    }
  });
  return map;
}

export function enrichLineItemsWithServiceNames(rows, serviceMap) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const existingName = String(
      row?.item_name ?? row?.itemName ?? row?.service_name ?? "",
    ).trim();
    if (existingName && existingName !== "--" && existingName !== "-") {
      return row;
    }
    const serviceId = row?.items ?? row?.item_id ?? row?.service_id;
    const resolved =
      serviceId != null ? serviceMap.get(String(serviceId)) || "" : "";
    if (!resolved) return row;
    return {
      ...row,
      item_name: resolved,
      itemName: resolved,
    };
  });
}

/**
 * After `payment_details/` success, build `location.state` for `ViewInvoice` (same shape as InvoicePayment).
 */
export async function fetchViewInvoiceLocationState({
  baseUrl,
  headers,
  invoiceNo,
  payment,
  paymentAmount,
  paymentType,
  invoiceDataFallback = {},
  serviceMaster = [],
  patientMaster = [],
}) {
  const serviceNameMap = buildServiceNameByIdMap(serviceMaster);

  const paymentMapped = {
    payment_date: payment?.created_at || "N/A",
    receipt_no: payment?.receipt_number || "N/A",
    payment_mode: payment?.mode_of_payment || paymentType || "N/A",
    ref_no: payment?.transaction_ref_no || "N/A",
    amount_paid: payment?.payment_amount || paymentAmount || "0.00",
  };

  let matchingInvoice = null;
  let allPaymentDetails = [];
  let lineItemsFromServer = [];

  try {
    const hip_id = sessionStorage.getItem("facility_name") || "";
    const paymentsResp = await axios.post(
      `${baseUrl}get_all_invoices/`,
      { hip_id, is_pagination_required: false },
      { headers },
    );
    const allInvoices = Array.isArray(paymentsResp?.data?.invoices)
      ? paymentsResp.data.invoices
      : [];
    matchingInvoice = allInvoices.find(
      (inv) =>
        inv?.invoice_number === invoiceNo || inv?.invoice_id === invoiceNo,
    );
    if (matchingInvoice) {
      allPaymentDetails = Array.isArray(matchingInvoice.payment_details)
        ? matchingInvoice.payment_details
        : Array.isArray(matchingInvoice.payments)
          ? matchingInvoice.payments
          : [];
      lineItemsFromServer = enrichLineItemsWithServiceNames(
        pickInvoiceLineItems(matchingInvoice),
        serviceNameMap,
      );
    }
  } catch (fetchErr) {
    console.warn(
      "fetchViewInvoiceLocationState: get_all_invoices failed",
      fetchErr,
    );
  }

  const base = matchingInvoice || invoiceDataFallback;
  const items = enrichLineItemsWithServiceNames(
    pickInvoiceLineItems(base),
    serviceNameMap,
  );

  if (!allPaymentDetails.length) {
    const existingPayments = collectInvoicePayments(base);
    const merged = [...existingPayments];
    if (payment) {
      const exists = merged.some(
        (p) =>
          (payment.payment_id != null &&
            p?.payment_id === payment.payment_id) ||
          (payment.receipt_number &&
            p?.receipt_number === payment.receipt_number),
      );
      if (!exists) merged.push(payment);
    }
    allPaymentDetails = merged;
  } else {
    allPaymentDetails = collectInvoicePayments({
      payment_details: allPaymentDetails,
    });
  }

  const rawItemsList =
    lineItemsFromServer.length > 0 ? lineItemsFromServer : items || [];

  const viewInvoiceData = {
    ...base,
    ...invoiceDataFallback,
    invoice_number:
      base?.invoice_number ||
      invoiceDataFallback?.invoice_number ||
      invoiceNo,
    patient_name:
      base?.patient_name ||
      invoiceDataFallback?.patient_name ||
      invoiceDataFallback?.patientName ||
      "N/A",
    patient_id:
      base?.patient_id ||
      invoiceDataFallback?.patient_id ||
      invoiceDataFallback?.patient,
    itemsList: rawItemsList,
    facility_address: base?.facility_address || "",
    center_address: base?.center_address || "",
    center: base?.center || "",
    facility_name: base?.facility_name || "",
    payment_status:
      payment?.status || base?.payment_status || invoiceDataFallback?.payment_status || "PAID",
    ...paymentMapped,
    payment,
    payment_details: allPaymentDetails,
  };

  const enrichOpts =
    getInvoicePaymentTotal(viewInvoiceData) > 0
      ? {}
      : { paymentAmountHint: paymentAmount };
  viewInvoiceData.itemsList = enrichLineItemsFromInvoiceTotals(
    viewInvoiceData.itemsList,
    viewInvoiceData,
    enrichOpts,
  );

  return {
    data: viewInvoiceData,
    serviceMaster,
    patientMaster,
  };
}
